import { useEffect, useState } from "react";
import { X, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CityAutocomplete } from "@/components/map/CityAutocomplete";
import { api } from "@/lib/api";
import { slug } from "@/lib/geo";

export const WishCitiesDialog = ({ w, b, index, children }) => {
  const [open, setOpen] = useState(false);
  const [cities, setCities] = useState([]);
  const [places, setPlaces] = useState([]);
  const [popular, setPopular] = useState([]);

  useEffect(() => {
    if (!open) return;
    setCities(w.cities || []);
    setPlaces(w.places || []);
    api.topCities(w.country, 0, 24).then((r) => setPopular(r.items)).catch(() => setPopular([]));
  }, [open, w]);

  const add = (name, place) => {
    if (cities.includes(name)) return;
    setCities([...cities, name]);
    if (place) setPlaces([...places.filter((p) => p.name !== name), place]);
  };
  const remove = (name) => { setCities(cities.filter((c) => c !== name)); setPlaces(places.filter((p) => p.name !== name)); };
  const save = async () => {
    await b.patchWish(w.id, { cities, places }, `Cities to visit in ${w.country} saved`);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-md" data-testid={`wish-cities-dialog-${index}`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Where in {w.country}?</DialogTitle>
          <DialogDescription>Pick the cities and places you want to see.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-1.5 min-h-[28px]" data-testid="wish-cities-picked">
          {cities.length === 0 && <span className="text-xs text-slate-500">No cities picked yet.</span>}
          {cities.map((c) => (
            <span key={c} className="flex items-center gap-1 pl-2.5 pr-1 py-0.5 rounded-full bg-amber-400/15 text-amber-100 text-xs">
              {c}
              <button onClick={() => remove(c)} className="p-0.5 hover:text-white" aria-label={`Remove ${c}`} data-testid={`wish-city-remove-${slug(c)}`}><X size={11} /></button>
            </span>
          ))}
        </div>
        <CityAutocomplete country={w.country} exclude={cities} onPick={add} />
        {popular.filter((c) => !cities.includes(c.name)).length > 0 && (
          <div>
            <p className="eyebrow !text-slate-500 mb-2">Popular</p>
            <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto">
              {popular.filter((c) => !cities.includes(c.name)).map((c) => (
                <button key={`${c.name}-${c.x}`} onClick={() => add(c.name, { name: c.name, x: c.x, y: c.y })} data-testid={`wish-city-popular-${slug(c.name)}`}
                  className="flex items-center gap-1 px-2 py-0.5 rounded-full border border-white/10 text-xs text-slate-300 hover:border-amber-400/50 hover:text-amber-200 transition-colors">
                  <Plus size={11} /> {c.name}
                </button>
              ))}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button onClick={save} className="bg-amber-500 text-[#1a1203] hover:bg-amber-400" data-testid="wish-cities-save-button">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
